import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

export const Fade = ({ children, delay = 0, y = 28, className = "", ...rest }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-8% 0px" }}
        transition={{ duration: 0.9, ease: EASE, delay }}
        {...rest}
    >
        {children}
    </motion.div>
);

const MaskedLine = ({ children, lineClassName = "", delay = 0, immediate = false }) => (
    <motion.span
        className="block overflow-hidden pb-[0.08em]"
        initial="hidden"
        {...(immediate
            ? { animate: "show" }
            : { whileInView: "show", viewport: { once: true, margin: "-8% 0px" } })}
    >
        <motion.span
            className={`block ${lineClassName}`}
            variants={{ hidden: { y: "112%" }, show: { y: 0 } }}
            transition={{ duration: 1.05, ease: EASE, delay }}
        >
            {children}
        </motion.span>
    </motion.span>
);

export const MaskedLines = ({
    lines,
    className = "",
    lineClassName = "",
    delay = 0,
    stagger = 0.12,
    immediate = false,
}) => (
    <div className={className}>
        {lines.map((line, i) => (
            <MaskedLine
                key={i}
                lineClassName={lineClassName}
                delay={delay + i * stagger}
                immediate={immediate}
            >
                {line}
            </MaskedLine>
        ))}
    </div>
);

export const Chapter = ({ index, title, dark = false }) => (
    <Fade className="flex items-center gap-4 md:gap-6" y={16}>
        <span
            className={`font-display text-sm font-semibold tracking-[0.22em] ${
                dark ? "text-sky" : "text-cobalt"
            }`}
        >
            {index}
        </span>
        <span className={`h-px flex-1 ${dark ? "bg-white/15" : "bg-ink/10"}`} />
        <span
            className={`text-[11px] md:text-xs uppercase tracking-[0.3em] ${
                dark ? "text-sky/70" : "text-slate"
            }`}
        >
            {title}
        </span>
    </Fade>
);

export const Counter = ({ value, className = "", pad = 2 }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-15% 0px" });
    const [n, setN] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, value, {
            duration: 1.8,
            ease: EASE,
            onUpdate: (v) => setN(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, value]);

    return (
        <span ref={ref} className={className}>
            {String(n).padStart(pad, "0")}
        </span>
    );
};
